import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";

const KEY = "s360_cookie_consent";

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [configure, setConfigure] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setVisible(true);
    const reopen = () => setVisible(true);
    window.addEventListener("s360-open-cookies", reopen);
    return () => window.removeEventListener("s360-open-cookies", reopen);
  }, []);

  const save = (prefs) => {
    localStorage.setItem(KEY, JSON.stringify({ necessary: true, ...prefs, date: new Date().toISOString() }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-md z-[60] bg-slate-950 text-white border border-slate-700 rounded-2xl shadow-2xl p-5"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Consentimiento de cookies"
        >
          <div className="flex items-start gap-3">
            <Cookie className="text-amber-400 shrink-0 mt-0.5" size={20} />
            <div className="text-sm">
              <p className="font-semibold mb-1">Tu privacidad importa</p>
              <p className="text-slate-300 text-xs leading-relaxed">
                Usamos cookies esenciales para que la web funcione y, con tu permiso, cookies de
                análisis y publicidad. Lee la{" "}
                <Link to="/legal/cookies" className="underline text-amber-400">política de cookies</Link>.
              </p>

              {configure && (
                <div className="mt-3 space-y-2 text-xs" data-testid="cookie-config-panel">
                  <label className="flex items-center gap-2 text-slate-300">
                    <input type="checkbox" checked disabled className="accent-amber-500" /> Esenciales (siempre activas)
                  </label>
                  <label className="flex items-center gap-2 text-slate-300">
                    <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="accent-amber-500" data-testid="cookie-analytics-toggle" /> Análisis
                  </label>
                  <label className="flex items-center gap-2 text-slate-300">
                    <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="accent-amber-500" data-testid="cookie-marketing-toggle" /> Publicidad
                  </label>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => save({ analytics: true, marketing: true })}
                  data-testid="cookie-accept-all"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs px-4 py-2 rounded-lg transition-colors"
                >
                  Aceptar todas
                </button>
                {configure ? (
                  <button
                    onClick={() => save({ analytics, marketing })}
                    data-testid="cookie-save-preferences"
                    className="bg-slate-700 hover:bg-slate-600 font-semibold text-xs px-4 py-2 rounded-lg transition-colors"
                  >
                    Guardar preferencias
                  </button>
                ) : (
                  <button
                    onClick={() => setConfigure(true)}
                    data-testid="cookie-configure"
                    className="bg-slate-700 hover:bg-slate-600 font-semibold text-xs px-4 py-2 rounded-lg transition-colors"
                  >
                    Configurar
                  </button>
                )}
                <button
                  onClick={() => save({ analytics: false, marketing: false })}
                  data-testid="cookie-reject-all"
                  className="text-slate-400 hover:text-white font-semibold text-xs px-3 py-2 transition-colors"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
